import React from 'react';
import {
  isValidDateRange,
} from 'utils/date';
import isNil from 'lodash/isNil';
import dayjs from 'utils/dayjs';
import clsx from 'clsx';
import {
  PickerDateRange,
  DateRangePreset,
} from 'core/components/DateTimePicker/types';
import {
  DateRange,
} from 'types/dateRange';
import RangePicker from 'core/components/DateTimePicker/base/RangePicker';

type Part = 'root' | 'rangePicker';

type Props = {
  value?: DateRange | null;
  onChange: (value: DateRange | null) => void;
  allowEmpty?: boolean;
  disabled?: boolean;
  maxDate?: Date | null;
  minDate?: Date | null;
  showPresets?: boolean;
  placeholder?: string;
  autoFocusDateInput?: boolean;
  classes?: Partial<Record<Part, string>>;
};

const getDefaultPresets = (maxDate?: Date | null): DateRangePreset[] => {
  const anchorDate = dayjs(maxDate ?? new Date());
  const endOfToday = anchorDate.endOf('day').toDate();
  return [
    {
      label: 'Today',
      dateRange: {
        from: anchorDate.startOf('day').toDate(),
        to: endOfToday,
      },
    },
    {
      label: 'Last 7 days',
      dateRange: {
        from: anchorDate.subtract(7, 'days').startOf('day').toDate(),
        to: endOfToday,
      },
    },
    {
      label: 'Last 30 days',
      dateRange: {
        from: anchorDate.subtract(30, 'days').startOf('day').toDate(),
        to: endOfToday,
      },
    },
    {
      label: 'Last 3 months',
      dateRange: {
        from: anchorDate.subtract(3, 'months').startOf('day').toDate(),
        to: endOfToday,
      },
    },
    {
      label: 'Last 6 months',
      dateRange: {
        from: anchorDate.subtract(6, 'months').startOf('day').toDate(),
        to: endOfToday,
      },
    },
    {
      label: 'This month',
      dateRange: {
        from: anchorDate.startOf('month').toDate(),
        to: endOfToday,
      },
    },
    {
      label: 'This year',
      dateRange: {
        from: anchorDate.startOf('year').toDate(),
        to: endOfToday,
      },
    },
    {
      label: 'Last year',
      dateRange: {
        from: anchorDate.subtract(1, 'year').startOf('year').toDate(),
        to: anchorDate.subtract(1, 'year').endOf('year').toDate(),
      },
    },
  ];
};

export default function DateRangePickerWrapper({
  value,
  onChange,
  allowEmpty = false,
  disabled = false,
  maxDate = dayjs().endOf('day').toDate(),
  minDate,
  showPresets = true,
  placeholder,
  autoFocusDateInput = false,
  classes,
}: Props) {
  const { min, max } = value ?? {};
  return (
    <div className={clsx('flex items-center gap-x-4', classes?.root)}>
      <RangePicker
        disabled={disabled}
        presets={showPresets ? getDefaultPresets(maxDate) : undefined}
        toDate={maxDate ?? undefined}
        fromDate={minDate ?? undefined}
        allowEmpty={allowEmpty}
        value={{
          from: min,
          to: max,
        }}
        onChange={(dateRange: PickerDateRange | undefined) => {
          const { from, to } = dateRange ?? {};
          const nextDateRange = { min: from, max: to };
          if (allowEmpty && isNil(from) && isNil(to)) {
            onChange(null);
          } else if (isValidDateRange(nextDateRange)) {
            onChange(nextDateRange);
          }
        }}
        numberOfMonths={2}
        placeholder={placeholder}
        autoFocusDateInput={autoFocusDateInput}
        className={clsx('w-56', classes?.rangePicker)}
      />
    </div>
  );
}
